import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {redirect} from "@tanstack/react-router";
import {CONFIG_PAGE_ROUTE_TO, configPageUrlPattern} from "~/workspaceConfig/configPageRoutes";
import {
    buildWorkspaceConfigEntryHref,
    hasWorkspaceUiConfig,
    resolveWorkspaceConfigEntry,
} from "~/workspaceConfig/configRuntime";

/** UI previews and devtools — allowed everywhere except production. */
export function isNonProductionEnvironment(environment: string): boolean {
    return environment.trim().toLowerCase() !== "prod";
}

const WORKSPACE_ROOT_URL_PATTERN = /^\/w\/[a-zA-Z0-9_-]+\/?$/;

export function isWorkspaceRootPath(pathname: string, workspaceId: Uuid): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    return normalized === `/w/${workspaceId}`;
}

/**
 * Workspace home is the scaffold `entryPageId` for this role.
 * Dashboard is a config page type — it is home only when it is the entry page.
 */
export function resolveWorkspaceHomeHref(workspaceId: Uuid, workspaceUserTemplateId: Uuid): string {
    if (hasWorkspaceUiConfig(workspaceId) !== true) {
        return `/w/${workspaceId}`;
    }
    try {
        return buildWorkspaceConfigEntryHref(workspaceId, workspaceUserTemplateId);
    } catch {
        return `/w/${workspaceId}`;
    }
}

export function resolveWorkspaceHomeUrlPattern(workspaceId: Uuid, workspaceUserTemplateId: Uuid): RegExp {
    if (hasWorkspaceUiConfig(workspaceId) !== true) {
        return WORKSPACE_ROOT_URL_PATTERN;
    }
    try {
        const entry = resolveWorkspaceConfigEntry(workspaceId, workspaceUserTemplateId);
        return configPageUrlPattern(entry.pageKey);
    } catch {
        return WORKSPACE_ROOT_URL_PATTERN;
    }
}

/** `/w/{workspaceId}` → config scaffold `entryPageId`. */
export function throwRedirectToWorkspaceConfigEntry(workspaceId: Uuid, workspaceUserTemplateId: Uuid): never {
    const entry = resolveWorkspaceConfigEntry(workspaceId, workspaceUserTemplateId);
    throw redirect({
        to: CONFIG_PAGE_ROUTE_TO,
        params: {
            workspaceId: workspaceId,
            pageKey: entry.pageKey,
        },
        search: {},
        replace: true,
    });
}
